import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });
export { client };

client.interceptors.request.use((config) => {
  // Only inject the admin token when the caller has not already provided
  // an explicit Authorization header (e.g. loyalty endpoints pass a
  // separate loyalty JWT). Otherwise the admin token would clobber the
  // loyalty one and the backend would reject the request with 403
  // "Not a loyalty session".
  const already = config.headers && (config.headers.Authorization || config.headers.authorization);
  if (already) return config;
  const token = localStorage.getItem("admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---- Public ----
export async function fetchRestaurant() {
  const { data } = await client.get("/restaurant");
  return data;
}

export async function fetchMenu(includeHidden = false) {
  const url = includeHidden ? "/menu?include_hidden=1" : "/menu";
  const { data } = await client.get(url);
  return data;
}

export async function fetchMenus(includeHidden = false) {
  const url = includeHidden ? "/menus?include_hidden=1" : "/menus";
  const { data } = await client.get(url);
  return data;
}

export async function fetchMenuFull(menuId, includeHidden = false) {
  const url = includeHidden ? `/menus/${menuId}/full?include_hidden=1` : `/menus/${menuId}/full`;
  const { data } = await client.get(url);
  return data;
}

// ---- Menu admin ----
export async function createMenu(payload) {
  const { data } = await client.post("/menus", payload);
  return data;
}
export async function updateMenu(id, payload) {
  const { data } = await client.put(`/menus/${id}`, payload);
  return data;
}
export async function deleteMenu(id) {
  await client.delete(`/menus/${id}`);
}

export async function reorderMenus(items) {
  const { data } = await client.post("/menus/reorder", items);
  return data;
}

export async function reorderCategories(items) {
  const { data } = await client.post("/categories/reorder", items);
  return data;
}

export async function reorderDishes(items) {
  const { data } = await client.post("/dishes/reorder", items);
  return data;
}

export async function translateText(text, source_lang = "it", target_langs = ["en","es","fr","de"]) {
  const { data } = await client.post("/translate", { text, source_lang, target_langs });
  return data.translations;
}

export async function startBulkTranslate() {
  const { data } = await client.post("/translate/bulk");
  return data;  // {job_id, total, status}
}

export async function getTranslateJob(jobId) {
  const { data } = await client.get(`/translate/bulk/${jobId}`);
  return data;
}

export async function translateAllDishes() {
  const { data } = await client.post("/translate/bulk", {}, { timeout: 20 * 60 * 1000 });
  return data;
}

// ---- Auth ----
export async function login(password) {
  const { data } = await client.post("/auth/login", { password });
  localStorage.setItem("admin_token", data.token);
  localStorage.setItem("admin_token_exp", data.expires_at);
  return data;
}

export function logout() {
  localStorage.removeItem("admin_token");
  localStorage.removeItem("admin_token_exp");
}

export async function verifyToken() {
  try {
    await client.get("/auth/verify");
    return true;
  } catch (e) {
    return false;
  }
}

export function hasToken() {
  return Boolean(localStorage.getItem("admin_token"));
}

// ---- Restaurant admin ----
export async function updateRestaurant(payload) {
  const { data } = await client.put("/restaurant", payload);
  return data;
}

export async function refreshReviewPlatforms() {
  const { data } = await client.post("/admin/reviews-platforms/refresh");
  return data;
}

// ---- Categories admin ----
export async function createCategory(payload) {
  const { data } = await client.post("/categories", payload);
  return data;
}
export async function updateCategory(id, payload) {
  const { data } = await client.put(`/categories/${id}`, payload);
  return data;
}
export async function deleteCategory(id) {
  await client.delete(`/categories/${id}`);
}

// ---- Dishes admin ----
export async function createDish(payload) {
  const { data } = await client.post("/dishes", payload);
  return data;
}
export async function updateDish(id, payload) {
  const { data } = await client.put(`/dishes/${id}`, payload);
  return data;
}
export async function deleteDish(id) {
  await client.delete(`/dishes/${id}`);
}

// ---- Password change ----
export async function changePassword(current_password, new_password) {
  const { data } = await client.post("/auth/change-password", { current_password, new_password });
  return data;
}

// ---- Upload ----
export async function uploadImage(file, onProgress) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await client.post("/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
    },
  });
  return data.url;
}

// ---- Bulk ----
export async function resetMenu() {
  const { data } = await client.post("/menu/reset");
  return data;
}

// ---- Reviews ----
const _ITALIAN_MONTHS = ["gennaio","febbraio","marzo","aprile","maggio","giugno","luglio","agosto","settembre","ottobre","novembre","dicembre"];
function _parseDateLabel(s) {
  // Best-effort parser for the free-text `date` field. Recognises "Marzo 2026",
  // "3/2026", "03-2026", "2026-03-15", … Falls back to {month: "", year: ""}.
  if (!s) return { month: "", year: "" };
  const t = String(s).trim().toLowerCase();
  const iso = t.match(/^(\d{4})-(\d{1,2})/);
  if (iso) return { year: iso[1], month: String(parseInt(iso[2], 10)) };
  const num = t.match(/(\d{1,2})[\/-](\d{4})/);
  if (num) return { year: num[2], month: String(parseInt(num[1], 10)) };
  const y = t.match(/(19|20)\d{2}/)?.[0] || "";
  const monthIdx = _ITALIAN_MONTHS.findIndex((m) => t.includes(m));
  return { year: y, month: monthIdx >= 0 ? String(monthIdx + 1) : "" };
}
export async function fetchReviews(limit = 50) {
  // New backend returns {reviews: [{id, author_name, rating, text, date, source, order, visible}]}.
  // Map to the shape the ReviewsSection component already expects.
  const { data } = await client.get(`/reviews`);
  const list = (data?.reviews || []).slice(0, limit);
  return list.map((r) => {
    const { month, year } = _parseDateLabel(r.date);
    return {
      id: r.id,
      author_name: r.author_name,
      text: r.text,
      percent: (Number(r.rating) || 5) * 20, // 1..5 → 20..100
      month,
      year,
      source: r.source,
    };
  });
}

// ---- Cover upload ----
export async function uploadCover(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await client.post("/admin/cover", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

// ---- Snapshot reload (recovery) ----
export async function reloadSnapshot() {
  const { data } = await client.post("/admin/snapshot/reload");
  return data;
}

// ---- Spa / Centro Benessere ----
export async function fetchSpaConfig() {
  const { data } = await client.get("/spa/config");
  return data;
}
export async function fetchSpaAvailability(dateStr) {
  const { data } = await client.get(`/spa/availability?date_str=${encodeURIComponent(dateStr)}`);
  return data;
}
export async function createSpaBooking(payload) {
  const { data } = await client.post("/spa/bookings", payload);
  return data;
}
export async function fetchAdminSpaBookings(fromDate, toDate) {
  const { data } = await client.get(`/admin/spa/bookings?from_date=${fromDate}&to_date=${toDate}`);
  return data;
}
export async function cancelSpaBooking(id) {
  const { data } = await client.patch(`/admin/spa/bookings/${id}`, { status: "cancelled" });
  return data;
}
export async function updateSpaBooking(id, patch) {
  const { data } = await client.patch(`/admin/spa/bookings/${id}`, patch);
  return data;
}
export async function adminCreateSpaBooking(payload) {
  const { data } = await client.post(`/admin/spa/bookings`, payload);
  return data;
}
export async function deleteSpaBooking(id) {
  const { data } = await client.delete(`/admin/spa/bookings/${id}`);
  return data;
}

// ---- Chiusure slot (Spa / Massaggi) ----
export async function fetchAdminBlocks(service, fromDate, toDate) {
  const { data } = await client.get(`/admin/blocks?service=${service}&from_date=${fromDate}&to_date=${toDate}`);
  return data;
}
export async function createAdminBlock(payload) {
  const { data } = await client.post(`/admin/blocks`, payload);
  return data;
}
export async function deleteAdminBlock(id) {
  const { data } = await client.delete(`/admin/blocks/${id}`);
  return data;
}
export async function deleteAdminDayBlocks(service, date) {
  const { data } = await client.delete(`/admin/blocks?service=${service}&date=${date}`);
  return data;
}
export async function createAdminBlockRange(payload) {
  const { data } = await client.post(`/admin/blocks/range`, payload);
  return data;
}

// ---- Massaggi ----
export async function fetchMassageConfig() {
  const { data } = await client.get("/massage/config");
  return data;
}
export async function fetchMassageAvailability(dateStr) {
  const { data } = await client.get(`/massage/availability?date_str=${encodeURIComponent(dateStr)}`);
  return data;
}
export async function createMassageBooking(payload) {
  const { data } = await client.post("/massage/bookings", payload);
  return data;
}
export async function fetchAdminMassageBookings(fromDate, toDate) {
  const { data } = await client.get(`/admin/massage/bookings?from_date=${fromDate}&to_date=${toDate}`);
  return data;
}
export async function updateMassageBooking(id, patch) {
  const { data } = await client.patch(`/admin/massage/bookings/${id}`, patch);
  return data;
}
export async function adminCreateMassageBooking(payload) {
  const { data } = await client.post(`/admin/massage/bookings`, payload);
  return data;
}
export async function cancelMassageBooking(id) {
  const { data } = await client.patch(`/admin/massage/bookings/${id}`, { status: "cancelled" });
  return data;
}
export async function deleteMassageBooking(id) {
  const { data } = await client.delete(`/admin/massage/bookings/${id}`);
  return data;
}

// ---- Domotica / Rooms ----
export async function fetchRoomPublic(slug) {
  const { data } = await client.get(`/rooms/${slug}`);
  return data;
}
export async function unlockRoom(slug, pin) {
  const { data } = await client.post(`/rooms/${slug}/unlock`, { pin });
  return data;
}
export async function triggerRoomAction(slug, actionId, pin) {
  const { data } = await client.post(`/rooms/${slug}/actions/${actionId}/trigger`, { pin });
  return data;
}
export async function fetchAdminRooms() {
  const { data } = await client.get(`/admin/rooms`);
  return data;
}
export async function fetchAdminQuickRequests() {
  const { data } = await client.get(`/admin/room-quick-requests`);
  return data;
}
export async function saveAdminQuickRequests(payload) {
  const { data } = await client.put(`/admin/room-quick-requests`, payload);
  return data;
}
export async function resetAdminQuickRequests() {
  const { data } = await client.post(`/admin/room-quick-requests/reset`);
  return data;
}
export async function adminCreateRoom(payload) {
  const { data } = await client.post(`/admin/rooms`, payload);
  return data;
}
export async function adminUpdateRoom(id, payload) {
  const { data } = await client.patch(`/admin/rooms/${id}`, payload);
  return data;
}
export async function adminDeleteRoom(id) {
  const { data } = await client.delete(`/admin/rooms/${id}`);
  return data;
}
export async function adminDedupeRooms() {
  const { data } = await client.post(`/admin/rooms/dedupe`);
  return data;
}
export async function adminTestAction(roomId, actionId) {
  const { data } = await client.post(`/admin/rooms/${roomId}/test-action/${actionId}`);
  return data;
}


// ---- POIs / Punti di interesse ----
export async function fetchPois(category, origin) {
  const params = new URLSearchParams();
  if (category && category !== "all") params.set("category", category);
  if (origin && origin.lat && origin.lon) {
    params.set("origin_lat", origin.lat);
    params.set("origin_lon", origin.lon);
  }
  const qs = params.toString() ? `?${params.toString()}` : "";
  const { data } = await client.get(`/pois${qs}`);
  return data;
}
export async function fetchAdminPois() {
  const { data } = await client.get(`/admin/pois`);
  return data;
}
export async function adminCreatePoi(payload) {
  const { data } = await client.post(`/admin/pois`, payload);
  return data;
}
export async function adminUpdatePoi(id, payload) {
  const { data } = await client.patch(`/admin/pois/${id}`, payload);
  return data;
}
export async function adminDeletePoi(id) {
  const { data } = await client.delete(`/admin/pois/${id}`);
  return data;
}





// ---- Loyalty (guest fidelity programme) ----
const LOYALTY_TOKEN_KEY = "loyalty_token";
export function loyaltyToken() { return localStorage.getItem(LOYALTY_TOKEN_KEY); }
export function setLoyaltyToken(t) { if (t) localStorage.setItem(LOYALTY_TOKEN_KEY, t); else localStorage.removeItem(LOYALTY_TOKEN_KEY); }
function loyaltyHeaders() {
  const t = loyaltyToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}
export async function loyaltyRegister(payload) {
  const { data } = await client.post("/loyalty/register", payload);
  setLoyaltyToken(data.token);
  return data;
}
export async function loyaltyLogin(payload) {
  const { data } = await client.post("/loyalty/login", payload);
  setLoyaltyToken(data.token);
  return data;
}
export function loyaltyLogout() { setLoyaltyToken(null); }
export async function loyaltyExport() {
  const { data } = await client.get("/loyalty/me/export", { headers: loyaltyHeaders() });
  return data;
}
export async function loyaltyDeleteAccount() {
  const { data } = await client.delete("/loyalty/me", { headers: loyaltyHeaders() });
  setLoyaltyToken(null);
  return data;
}
export async function adminPrivacyExport(phone, email) {
  const { data } = await client.get("/admin/privacy/export", { params: { phone: phone || "", email: email || "" } });
  return data;
}
export async function adminPrivacyErase(phone, email, reason) {
  const { data } = await client.post("/admin/privacy/erase", { phone: phone || null, email: email || null, reason });
  return data;
}
export async function loyaltyMe() {
  const { data } = await client.get("/loyalty/me", { headers: loyaltyHeaders() });
  return data;
}
export async function loyaltyReferral() {
  const { data } = await client.get("/loyalty/referral", { headers: loyaltyHeaders() });
  return data;
}
export async function loyaltyRedeemCode(code) {
  const { data } = await client.post("/loyalty/redeem-code", { code }, { headers: loyaltyHeaders() });
  return data;
}
export async function loyaltyRedeemReward(reward_id) {
  const { data } = await client.post("/loyalty/redeem-reward", { reward_id }, { headers: loyaltyHeaders() });
  return data;
}
export async function loyaltyListRewards() {
  const { data } = await client.get("/loyalty/rewards");
  return data.rewards || [];
}
// ---- Loyalty Web Push ----
export async function loyaltyMagicLogin(token) {
  const { data } = await client.post("/loyalty/magic-login", { token });
  return data;
}
export async function loyaltySetPassword(password) {
  const { data } = await client.post("/loyalty/set-password", { password }, { headers: loyaltyHeaders() });
  return data;
}
export async function loyaltyPushVapidKey() {
  const { data } = await client.get("/loyalty/push/vapid-public-key");
  return data.key || "";
}
export async function loyaltyPushSubscribe(subscription) {
  const { data } = await client.post(
    "/loyalty/push/subscribe",
    { endpoint: subscription.endpoint, keys: subscription.keys },
    { headers: loyaltyHeaders() },
  );
  return data;
}
export async function loyaltyPushUnsubscribe(endpoint) {
  const { data } = await client.delete(
    `/loyalty/push/subscribe?endpoint=${encodeURIComponent(endpoint)}`,
    { headers: loyaltyHeaders() },
  );
  return data;
}
export async function loyaltyPushTest() {
  const { data } = await client.post("/loyalty/push/test", {}, { headers: loyaltyHeaders() });
  return data;
}
// Admin
export async function adminListLoyaltyUsers() {
  const { data } = await client.get("/admin/loyalty/users");
  return data;
}
export async function adminAddStamps(user_id, count, note) {
  const { data } = await client.post("/admin/loyalty/add-stamps", { user_id, count, note });
  return data;
}
export async function adminDeleteLoyaltyUser(user_id) {
  const { data } = await client.delete(`/admin/loyalty/users/${user_id}`);
  return data;
}
export async function adminUpdateLoyaltyUser(user_id, patch) {
  const { data } = await client.patch(`/admin/loyalty/users/${user_id}`, patch);
  return data;
}
export async function adminResetAllLoyaltyBalances() {
  const { data } = await client.post("/admin/loyalty/reset-all-balances");
  return data;
}
export async function adminLoyaltyWelcomeInvite(payload) {
  const { data } = await client.post("/admin/loyalty/welcome-invite", payload);
  return data;
}
export async function adminGrantMissingWelcome() {
  const { data } = await client.post("/admin/loyalty/grant-missing-welcome");
  return data;
}
export async function adminRecomputeCashback() {
  const { data } = await client.post("/admin/loyalty/recompute-cashback");
  return data;
}
export async function adminAddLoyaltyCredit(user_id, amount_euros, note) {
  const { data } = await client.post("/admin/loyalty/add-credit", { user_id, amount_euros, note });
  return data;
}
export async function adminRedeemLoyaltyCredit(user_id, amount_euros, note) {
  const { data } = await client.post("/admin/loyalty/redeem-credit", { user_id, amount_euros, note });
  return data;
}
export async function adminListCustomers() {
  const { data } = await client.get("/admin/customers");
  return data;
}
export async function adminAddManualCustomer(payload) {
  const { data } = await client.post("/admin/customers/manual", payload);
  return data;
}
export async function adminAddManualCustomerBulk(payload) {
  const { data } = await client.post("/admin/customers/manual/bulk", payload);
  return data;
}
export async function adminDeleteManualCustomer(manual_id) {
  const { data } = await client.delete(`/admin/customers/manual/${manual_id}`);
  return data;
}
// ---- Google Reviews (curated) ----
export async function adminListReviews() {
  const { data } = await client.get("/admin/reviews");
  return data.reviews || [];
}
export async function adminCreateReview(payload) {
  const { data } = await client.post("/admin/reviews", payload);
  return data;
}
export async function adminUpdateReview(id, payload) {
  const { data } = await client.patch(`/admin/reviews/${id}`, payload);
  return data;
}
export async function adminDeleteReview(id) {
  const { data } = await client.delete(`/admin/reviews/${id}`);
  return data;
}
export async function adminCreateLoyaltyCode(stamps, expires_in_days = 30) {
  const { data } = await client.post("/admin/loyalty/codes", { stamps, expires_in_days });
  return data;
}
export async function adminListLoyaltyCodes() {
  const { data } = await client.get("/admin/loyalty/codes");
  return data;
}
export async function adminListLoyaltyRewards() {
  const { data } = await client.get("/admin/loyalty/rewards");
  return data;
}
export async function adminCreateLoyaltyReward(payload) {
  const { data } = await client.post("/admin/loyalty/rewards", payload);
  return data;
}
export async function adminUpdateLoyaltyReward(id, payload) {
  const { data } = await client.patch(`/admin/loyalty/rewards/${id}`, payload);
  return data;
}
export async function adminDeleteLoyaltyReward(id) {
  const { data } = await client.delete(`/admin/loyalty/rewards/${id}`);
  return data;
}


// ---- Analytics ----
export function track(event) {
  // Fire-and-forget. Silence errors in production so analytics never breaks
  // the UI; log them in development to help debugging.
  try {
    client.post("/track", event).catch((err) => {
      if (process.env.NODE_ENV === "development") console.debug("track failed:", err);
    });
  } catch (err) {
    if (process.env.NODE_ENV === "development") console.debug("track threw:", err);
  }
}
export async function fetchAdminStats(days = 30) {
  const { data } = await client.get(`/admin/stats?days=${days}`);
  return data;
}


// ---- WhatsApp templates ----
export async function fetchWaTemplates() {
  const { data } = await client.get("/admin/wa-templates");
  return data;
}
export async function updateWaTemplate(key, body) {
  const { data } = await client.put(`/admin/wa-templates/${key}`, { body });
  return data;
}
export async function resetWaTemplate(key) {
  const { data } = await client.post(`/admin/wa-templates/${key}/reset`);
  return data;
}
export async function previewWaTemplate(key, body, ctx = {}) {
  const { data } = await client.post(`/admin/wa-templates/${key}/preview`, { body, ctx });
  return data;
}
// Log a WhatsApp send event so we can render a counter next to the
// send button in the customers list. Fire-and-forget from the UI —
// failure never blocks opening the wa.me link.
export async function logWaSend({ phone, email, name, template_key }) {
  const { data } = await client.post("/admin/customers/wa-log", { phone, email, name, template_key });
  return data;
}
export const fetchCampaignOverview = () => client.get("/admin/campaign/overview").then((r) => r.data);
export const buildCampaignMessage = (row, stage = "first") => client.post("/admin/campaign/message", { ...row, stage }).then((r) => r.data);
export const undoCampaignSend = (phone) => client.delete("/admin/campaign/sent", { params: { phone } }).then((r) => r.data);
export async function fetchWaHistory({ phone, email } = {}) {
  const params = new URLSearchParams();
  if (phone) params.set("phone", phone);
  if (email) params.set("email", email);
  const { data } = await client.get(`/admin/customers/wa-log?${params.toString()}`);
  return data;
}
export async function fetchWaStats(days = 7) {
  const { data } = await client.get(`/admin/customers/wa-log/stats?days=${days}`);
  return data;
}
// Do-Not-Contact toggling. Idempotent — POST adds/updates, DELETE removes.
export async function addDnc({ phone, email, name, reason }) {
  const { data } = await client.post("/admin/customers/dnc", { phone, email, name, reason });
  return data;
}
export async function removeDnc({ phone, email }) {
  const params = new URLSearchParams();
  if (phone) params.set("phone", phone);
  if (email) params.set("email", email);
  const { data } = await client.delete(`/admin/customers/dnc?${params.toString()}`);
  return data;
}

// Safe client-side interpolation used by pages that build wa.me links
// entirely on the frontend (Fidelity code send, generic customer WhatsApp).
// Missing keys resolve to empty string just like the backend helper.
export function renderTemplate(body, ctx = {}) {
  if (!body) return "";
  return body.replace(/\{(\w+)\}/g, (_, k) => {
    const v = ctx[k];
    return v == null ? "" : String(v);
  });
}
